/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import {
  Boton,
  Titulo,
  Formulario,
  accessAPI,
  Sidebar,
  tituloBase,
  TablaFiltrable,
  useFuncionalidad
} from "../../../../shared";

const SEARCH_FIELDS = ["nombre"];

// Sí / No como en la lista de tipos de actividad: check o cruz en círculo
const iconoSiNo = (valor) => (
  <i
    className={valor ? "pi pi-check-circle" : "pi pi-times-circle"}
    css={{ color: valor ? "var(--verde)" : "var(--rojo)", fontSize: "1.1rem" }}
  />
);

export default function HallazgoTipos() {
  // La configuración de reportes a DNI solo para organismos con la integración
  const conJuguetesDni = useFuncionalidad("JUGUETES_DNI");
  const [loader, setLoader] = useState(true);
  const [hallazgoTipos, setHallazgoTipos] = useState([]);
  const [sideBar, setSideBar] = useState(false);
  const [formLoader, setFormLoader] = useState(false);

  const toast = useRef(null);
  const navigate = useNavigate();

  function crearHallazgoTipo(camposFormulario) {
    // "Requiere análisis" y "Permite archivos" exigen "Cliente responde" (la
    // API también lo valida)
    if ((camposFormulario.requiereAnalisis || camposFormulario.permiteArchivos) && !camposFormulario.clienteResponde) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Para que el tipo requiera análisis o permita archivos, el cliente tiene que responder.",
        sticky: true
      });
      return;
    }
    setFormLoader(true);
    accessAPI(
      "POST",
      "admin/hallazgo/tipo",
      camposFormulario,
      (respuesta) => {
        navigate(`/hallazgotipo/${respuesta.id}`);
      },
      (respuesta) => {
        setFormLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al crear tipo de hallazgo",
          sticky: true
        });
      }
    );
  }

  const crearFormConfig = {
    secciones: [
      {
        nombre: "Datos del tipo de hallazgo",
        campos: [
          {
            nombre: "nombre",
            tipoitemformularioid: 1,
            etiqueta: "Nombre",
            requerido: true
          },
          {
            nombre: "clienteResponde",
            tipoitemformularioid: 6,
            etiqueta: "Cliente responde"
          },
          {
            nombre: "requiereAnalisis",
            tipoitemformularioid: 6,
            etiqueta: "Requiere análisis"
          },
          {
            nombre: "permiteArchivos",
            tipoitemformularioid: 6,
            etiqueta: "Permite archivos"
          },
          ...(conJuguetesDni
            ? [
                {
                  nombre: "enviarDNIJuguetes",
                  tipoitemformularioid: 6,
                  etiqueta: "Reportar a DNI - producto"
                },
                {
                  nombre: "enviarDNIJuguetesInspeccion",
                  tipoitemformularioid: 6,
                  etiqueta: "Reportar a DNI - inspección"
                }
              ]
            : [])
        ]
      }
    ],
    botones: [
      { etiqueta: "Crear tipo de hallazgo", callback: crearHallazgoTipo, validar: true }
    ]
  };

  useEffect(() => {
    document.title = "Tipos de Hallazgo";
    accessAPI(
      "GET",
      "admin/hallazgo/tipo",
      null,
      (respuesta) => {
        setHallazgoTipos(respuesta);
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg || "Error al cargar tipos de hallazgo",
          sticky: true
        });
        setLoader(false);
      }
    );
    return () => (document.title = tituloBase());
  }, []);

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Sidebar
        visible={sideBar}
        onHide={() => setSideBar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nuevo tipo de hallazgo</Titulo>
        <Formulario config={crearFormConfig} loader={formLoader} />
      </Sidebar>

      {loader && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem"
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem"
            }}
          >
            <Skeleton width="15rem" height="2.5rem" />
            <Skeleton width="16rem" height="2.5rem" />
          </div>
          <Card>
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem"
              }}
            >
              <Skeleton width="100%" height="3rem" />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} width="100%" height="3.5rem" />
              ))}
            </div>
          </Card>
        </div>
      )}

      {!loader && (
        <div>
          <Titulo
            urlVolver="/config"
            botones={
              <Boton callback={() => setSideBar(true)}>
                Nuevo tipo de hallazgo
              </Boton>
            }
          >
            Tipos de Hallazgo
          </Titulo>

          <Card>
            <TablaFiltrable
              value={hallazgoTipos}
              searchFields={SEARCH_FIELDS}
              anchoBusqueda="33%"
              nombreArchivo="tipos_hallazgo"
              emptyMessage="No hay tipos de hallazgo registrados"
              onRowClick={(e) => {
                if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
                  window.open(
                    `/hallazgotipo/${e.data.id}`,
                    "_blank"
                  );
                } else {
                  navigate(`/hallazgotipo/${e.data.id}`);
                }
              }}
              rows={10}
              css={{
                "& .p-datatable-tbody > tr": { cursor: "pointer" }
              }}
            >
              <Column
                header="Nombre"
                body={(rowData) => (
                  <span css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    {rowData.nombre}
                    {!rowData.activa && (
                      <Tag severity="danger" css={{ fontSize: "0.7rem" }}>
                        Deshabilitado
                      </Tag>
                    )}
                  </span>
                )}
              />
              <Column
                header="Cliente Responde"
                body={(rowData) => iconoSiNo(rowData.clienteResponde)}
                style={{ width: "10rem", textAlign: "center" }}
              />
              <Column
                header="Requiere análisis"
                body={(rowData) => iconoSiNo(rowData.requiereAnalisis)}
                style={{ width: "10rem", textAlign: "center" }}
              />
              <Column
                header="Permite archivos"
                body={(rowData) => iconoSiNo(rowData.permiteArchivos)}
                style={{ width: "10rem", textAlign: "center" }}
              />
            </TablaFiltrable>
          </Card>
        </div>
      )}
    </>
  );
}
